import { isNotDefined } from '@sgme/fp';
import { HStack } from '@sgme/ui';
import type { CustomCellRendererProps } from 'ag-grid-react';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  type OrderCommand,
  type OrderCommandButtonState,
  orderCommands,
  useButtonsState,
} from '@/App/Blotter/grids/order/useButtonsState';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import {
  isAlgoOrderType,
  OMSOrderTypeFromBlotterAlgoOrderType,
} from '@/store/blotter/api/types/BlotterOrderEntryFromBackend.ts';
import type { BlotterOrderEntry } from '@/store/blotter/types/BlotterOrderEntry.ts';
import { summonOrder } from '@/store/blotter/workflows/summonOrder/action.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import {
  useFillNowAlgoMutation,
  usePauseAlgoMutation,
  useResumeAlgoMutation,
} from '@/store/orders/algoOrders/api/index.ts';
import { selectActiveTabId, setTabType } from '@/store/tabs/slice.ts';
import { selectSelectedTabType } from '@/store/tabs/selectors.ts';

const dataE2eForCommandButton: { [command in OrderCommand]: string } = {
  view: 'view-btn',
  edit: 'edit-btn',
  cancel: 'cancel-btn',
  pauseOrResume: 'pauseOrResult-btn',
  fill: 'fill-btn',
};

export const CommandsCellRenderer = ({
  data: entry,
}: CustomCellRendererProps<BlotterOrderEntry, BlotterOrderEntry | undefined>) => {
  const dispatch = useAppDispatch();
  const activeTabId = useAppSelector(selectActiveTabId);
  const tabType = useAppSelector(selectSelectedTabType);

  const track = useTrackEvent();

  const [pauseAlgoCall, { isLoading: pauseAlgoIsInProgress }] = usePauseAlgoMutation();
  const [resumeAlgoCall, { isLoading: resumeAlgoIsInProgress }] = useResumeAlgoMutation();
  const [fillNowAlgoCall, { isLoading: fillAlgoIsInProgress }] = useFillNowAlgoMutation();

  const algoCommandIsInProgress = pauseAlgoIsInProgress || resumeAlgoIsInProgress || fillAlgoIsInProgress;

  const actionIcons = useMemo(
    () => ({
      view: 'visibility',
      edit: 'create',
      cancel: 'cancel',
      pauseOrResume: entry?.status === 'Paused' ? 'play_arrow' : 'pause',
      fill: 'fast_forward',
    }),
    [entry],
  );

  const buttonsState = useButtonsState(entry, algoCommandIsInProgress);

  const handleClick = useCallback(
    (actionType: OrderCommand) => {
      if (isNotDefined(entry) || entry.orderType === 'Lyrebird') {
        return;
      }

      if(tabType !== 'tiles'){
        dispatch(setTabType({ tabId: activeTabId, type: 'tiles' }));
      }

      track('Order action', `${actionType} ${entry.id}`);

      switch (actionType) {
        case 'cancel':
        // intentionnal no break, same behavior as 'view' for now
        // to avoid passing command boolean 'showCancellingConfirmation' in appstate
        case 'view': {
          dispatch(summonOrder(entry, 'VIEW', activeTabId));
          return;
        }
        case 'edit': {
          dispatch(summonOrder(entry, 'EDIT', activeTabId));
          return;
        }
        case 'pauseOrResume': {
          if (!isAlgoOrderType(entry.orderType)) {
            console.warn(`Pause/Resume action is only available for algo orders. Order ID: ${entry.id}`);
            return;
          }
          dispatch(summonOrder(entry, 'VIEW', activeTabId));
          (entry.status === 'Paused' ? resumeAlgoCall : pauseAlgoCall)({
            orderRef: entry.id,
            orderType: OMSOrderTypeFromBlotterAlgoOrderType[entry.orderType],
          });
          return;
        }
        case 'fill': {
          if (!isAlgoOrderType(entry.orderType)) {
            console.warn(`Fill action is only available for algo orders. Order ID: ${entry.id}`);
            return;
          }
          dispatch(summonOrder(entry, 'VIEW', activeTabId));
          fillNowAlgoCall({ orderRef: entry.id, orderType: OMSOrderTypeFromBlotterAlgoOrderType[entry.orderType] });
          return;
        }
      }
    },
    [entry, fillNowAlgoCall, pauseAlgoCall, resumeAlgoCall, activeTabId],
  );

  if (isNotDefined(entry)) {
    return null;
  }

  return (
    <HStack horizontalAlignment="start">
      {orderCommands.map((actionType) => (
        <OrderActionButton
          key={actionType}
          actionType={actionType}
          iconName={actionIcons[actionType]}
          dateE2e={dataE2eForCommandButton[actionType]}
          orderEntry={entry}
          state={buttonsState[actionType]}
          onClick={handleClick}
        />
      ))}
    </HStack>
  );
};

type OrderActionButtonProps = {
  actionType: OrderCommand;
  iconName: string;
  dateE2e: string;
  state: OrderCommandButtonState;
  orderEntry: BlotterOrderEntry;
  onClick: (actionype: OrderCommand) => void;
};

const OrderActionButton = ({ actionType, orderEntry, state, onClick, iconName, dateE2e }: OrderActionButtonProps) => {
  const { formatMessage } = useIntl();

  if (!state.present) {
    return null;
  }

  const tooltip = state.enabled
    ? formatMessage({ id: `blotter.tab.Order.column.actions.${actionType}` }, { status: orderEntry?.status })
    : state.reason;

  return (
    <span title={tooltip}>
      <button
        type="button"
        disabled={state.present ? !state.enabled : true}
        className="btn btn-sm btn-link btn-icon mx-0 me-1 px-0 border-0 mb-2"
        onClick={() => onClick(actionType)}
        data-e2e={dateE2e}
      >
        <i className="icon icon-sm">{iconName}</i>
      </button>
    </span>
  );
};
